import { FC, ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useDirection } from '@hooks';
import { useTrans } from '@libs/i18n';
import { XIcon } from '@phosphor-icons/react';
import { NoFlipBox } from '@utils';

import type { TooltipSizeTokens, TooltipVariantTokens } from './tooltipVariants';

export interface TooltipCardContentProps {
  /** Optional heading line. */
  title?: ReactNode;
  /** Body content — accepts rich nodes (text, progress bars, etc.). */
  description?: ReactNode;
  /** Leading icon shown next to the title (or the body when there's no title). */
  icon?: ReactNode;
  /** Footer node, typically action buttons. */
  actions?: ReactNode;
  /** Render an X close affordance in the header. */
  showClose?: boolean;
  /** Fired when the close affordance is clicked. */
  onClose?: () => void;
  /** Resolved colour tokens for the active variant. */
  tokens: TooltipVariantTokens;
  /** Resolved sizing tokens for the active size. */
  size: TooltipSizeTokens;
}

/**
 * Presentational inner layout shared by anchored and floating tooltips. Renders
 * a compact single-line hint when only `description` is supplied, and a richer
 * header / body / footer card when a title, icon, close button or actions are
 * present.
 */
export const TooltipCardContent: FC<TooltipCardContentProps> = observer(({
  title,
  description,
  icon,
  actions,
  showClose,
  onClose,
  tokens,
  size,
}) => {
  const { dir } = useDirection();
  const { t } = useTrans();
  const hasHeader = !!title || !!icon || !!showClose;

  return (
    <NoFlipBox
      mode={dir}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: size.gap,
        color: tokens.color,
        textAlign: 'start',
      }}
    >
      {hasHeader && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
            {icon && (
              <Box sx={{ display: 'flex', color: tokens.accentColor, lineHeight: 0 }}>{icon}</Box>
            )}
            {title && (
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, color: tokens.color, fontSize: size.fontSize }}
              >
                {title}
              </Typography>
            )}
          </Box>
          {showClose && (
            <Box
              component="button"
              type="button"
              onClick={onClose}
              aria-label={t('app.common.close')}
              sx={{
                border: 'none',
                background: 'none',
                p: 0.25,
                cursor: 'pointer',
                lineHeight: 0,
                color: tokens.color,
                opacity: 0.7,
                '&:hover': { opacity: 1 },
              }}
            >
              <XIcon size={14} />
            </Box>
          )}
        </Box>
      )}

      {description != null && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          {/* Icon sits with the body only when there is no header to host it. */}
          {!hasHeader && icon && (
            <Box sx={{ display: 'flex', color: tokens.accentColor, lineHeight: 0 }}>{icon}</Box>
          )}
          <Typography
            component="div"
            sx={{
              color: tokens.color,
              fontSize: size.fontSize,
              lineHeight: 1.5,
              fontWeight: title ? 400 : 500,
            }}
          >
            {description}
          </Typography>
        </Box>
      )}

      {actions && <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>{actions}</Box>}
    </NoFlipBox>
  );
});
